import { SelectedSlots, StandardFourTimeZoneId } from "./schedule";
import { VolunteerSelectedLanguageType, VolunteerSignupMentorStatusType } from "./volunteer-signup-mentor";

export interface BaseVolunteer {
	volunteerId: number,
	volunteerStatusId: number,
	canVolunteerInEnglish: boolean,
	canVolunteerInSpanish: boolean,
	isMentorApproved: boolean,
	isInClassApproved: boolean,
	isOfficeHoursApproved: boolean,
	attendedOrientation: boolean,
	interviewDateUTC: number | undefined,
	hasEnVideo: boolean,
	hasEsVideo: boolean,
	volunteerInterview?: VolunteerInterview,
	person: {
		id: number,
		fullName: string,
	},
	createdUTC: number,
	updatedUTC: number,
}

export interface Volunteer extends BaseVolunteer {
	hasLead: boolean,
	questionnaireLearnId: number | undefined,
	handbookSigned: boolean,
	mediaConsentSigned: boolean,
	agreementsSigned: 'Signed' | 'Partial' | 'Not Signed',
	lastContactUTC?: number,
	lastContactStaffId?: number,
	lastAutoEmailDeliveredUTC?: number,
	assignedToStaffId?: number,
	interviewScore: number | undefined,
}

export interface VolunteerProspect {
	leadVolunteerId: number,
	volunteerId: number,
	volunteerStatusId: number,
	willVolunteerInEnglish: boolean,
	willVolunteerInSpanish: boolean,
	availableStart: string | undefined,
	mentorInterest: boolean | undefined,
	inClassInterest: boolean | undefined,
	officeHoursInterest: boolean | undefined,
	questionnaireLearnId: number,
	interviewDateUTC: number | undefined,
	createdUTC: number,
	updatedUTC: number,
	lastContactUTC?: number,
	lastContactStaffId?: number,
	lastAutoEmailDeliveredUTC?: number,
	assignedToStaffId?: number,
	volunteerInterview?: VolunteerInterview,
	interviewScore: number | undefined,
	person: {
		id: number,
		fullName: string
	}
}

export interface VolunteerStatusLog {
	volunteerStatusLogId: number,
	fromVolunteerStatusId: number | undefined,
	toVolunteerStatusId: number,
	notes: string | undefined,
	updatedUTC: number,
	updatedByPersonId: number | undefined,
	updatedByStaffId: number | undefined,
	updatedByPersonName: string | undefined,
}

export interface VolunteerStatusForm {
	volunteerId: number,
	topics?: number[],
	roles?: string[],
	languages?: string[],
	fromVolunteerStatusId: number,
	toVolunteerStatusId: number,
	attendedOrientation?: boolean,
	notes?: string,
}

export interface VolunteerInterviewForm {
	missionAlignmentRating: number,
	experienceRating: number,
	scheduleRating: number,
	mentorshipStyleRating: number,
	presentationStyleRating: number,
	notes: string
}

export interface VolunteerInterview extends VolunteerInterviewForm {
	volunteerInterviewId: number,
	updatedUTC: number,
	updatedByStaffId: number | undefined,
}

export const VOLUNTEER_INTERVIEW_RATING_VALUE_MAP: { [index: number]: number } = {
	1: 0,
	2: 12.5,
	3: 16,
	4: 20,
};

export interface VolunteerMentorSignup {
	trimesterId: number,
	volunteerSignupMentorStatus: VolunteerSignupMentorStatusType,
	selectedLanguage: VolunteerSelectedLanguageType,
	assignedLanguageId: number | undefined,
	createdUTC: number,
	updatedUTC: number
}

export interface VolunteerDetails extends BaseVolunteer {
	willVolunteerInEnglish: boolean,
	willVolunteerInSpanish: boolean,
	availableStart: string | undefined,
	mentorInterest: boolean | undefined,
	inClassInterest: boolean | undefined,
	officeHoursInterest: boolean | undefined,
	isEntrepreneur: boolean | undefined,
	isStaff: boolean,
	isAlum: boolean,
	hasVideo: boolean,
	englishVideoUploadedUTC: number | undefined,
	spanishVideoUploadedUTC: number | undefined,
	mentorSchedule: {
		meetingsId: number,
		numMeetings: number,
		selectedMeetingSlots: SelectedSlots,
	} | undefined,
	questionnaireLearnId: number,
	placeAddress: string | undefined,
	leadVolunteerCreatedUTC: number,
	expertises: number[],
	volunteerTopics: VolunteerTopic[],
	socialLinks: { webLinkTypeId: number, url: string }[],
	volunteerStatusLogs: VolunteerStatusLog[],
	mentorSignups: VolunteerMentorSignup[],
	specialistSignups: {
		trimesterId: number,
		topics: {
			volunteerSignupSpecialistStatus: string,
			topicId: number,
			languageId: number,
			availableClassStartTimeUTC: number,
			availableClassEndTimeUTC: number,
		}[],
		createdUTC: number,
		updatedUTC: number
	}[],
	standardFourTimeZoneId: StandardFourTimeZoneId | undefined, // standardFourTimeZoneId associated with the volunteer's location
}

export interface VolunteerTopicForm {
	volunteerTopicId?: number,
	volunteerId: number,
	topicId: number,
	isApproved: boolean,
	note: string | undefined,
}

export interface VolunteerTopic extends VolunteerTopicForm {
	updatedUTC: number,
}